import * as React from 'react';

import {
  DeleteOutlined,
  EditFilled,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Form } from 'antd';
import {
  Column,
  Text,
  Notification,
  Tooltip,
  ButtonType,
} from 'farmhub-ui-core';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from 'app';
import {
  Empty,
  FilterTable,
  IconActionTable,
  NameText,
  Table,
  dataEmpty,
  StatusCellTable,
  CButton,
} from 'components';
import { useFetchTableData, useQueryParam } from 'hooks';
import {
  updateSavedListEntityReport,
  updateSavedPaginationEntityReport,
  filterEntityLarkBase,
  updateEntityLarkBase,
  deleteEntityLarkBase,
} from 'slice';
import {
  ENTITY_REPORT_STATUS,
  checkLoadPageWithID,
  formatDate,
  getDummyArray,
  hasLoadLastPagePagination,
  orderList,
  renderTableSkeleton,
  replaceItemInListOptimize,
} from 'utils';

import { statusList } from '../commons/data';
import { entityQuantityReportQuery } from '../commons/query';

const option = {
  isVisible: true,
  whiteList: ['order', 'take', 'page', 'where'],
};

const ACTION_TYPE = {
  CHANGE_STATUS: 'CHANGE_STATUS',
  DELETE: 'DELETE',
};

const EntityStockReportTable = (): JSX.Element => {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const reloadFlag = useAppSelector(state => state.entityReport.reloadFlag);
  const entityID = useAppSelector(state => state.account.item?.id);
  const [loadingActionRecordIDs, setLoadingActionRecordIDs] = React.useState<
    string[]
  >([]);
  const prevRoute = useAppSelector(state => state.appRoute.prevRoute);
  const savedList = useAppSelector(state => state.entityReport.savedList);
  const savedPagination = useAppSelector(
    state => state.entityReport.savedPagination
  );

  const [query, setQuery] = React.useState<any>({
    ...entityQuantityReportQuery,
    entityID,
  });

  const dataFilter = [
    {
      name: 'order',
      list: orderList,
    },
    {
      name: 'status',
      list: statusList,
      placeholder: 'Lọc theo trạng thái',
    },
  ];

  const checkNotLoadList = React.useMemo(
    () =>
      checkLoadPageWithID(savedList, prevRoute, '/stock-report/', '/update'),
    [prevRoute]
  );

  useQueryParam({ query, option, deps: [query] });

  const {
    isLoading,
    data: entityReportList,
    setData: setEntityReportList,
    onReFreshList: handleRefreshList,
    pagination,
    setPagination,
  } = useFetchTableData({
    action: filterEntityLarkBase,
    query,
    isVisible: !checkNotLoadList,
    deps: [query, reloadFlag, checkNotLoadList],
  });

  const handleFilterFormChange = e => {
    if (e.isObject) {
      return;
    }

    const key = Object.keys(e)[0];
    const value = Object.values(e)[0];

    if (value === 'all') {
      delete query[key];
      setQuery({ ...query });
    } else {
      if (key === 'name') {
        setQuery({ ...query, ...e, page: 1 });
      } else {
        setQuery({ ...query, ...e });
      }
    }
  };

  const handlePaginationChange = e => {
    setQuery({
      ...query,
      page: e.current,
      take: e.pageSize,
    });
  };

  const handleChangeStatus = async (id: string, status: number) => {
    const params = {
      id,
      body: {
        status,
      },
    };
    setLoadingActionRecordIDs(prevState => [
      ...prevState,
      `${ACTION_TYPE.CHANGE_STATUS}_${id}`,
    ]);

    try {
      const result: any = await dispatch(updateEntityLarkBase(params)).unwrap();
      const { data } = result;

      if (!query[status] && query[status] !== 0) {
        replaceItemInListOptimize(
          {
            id: data.id,
            status: data.status,
          },
          setEntityReportList
        );
      } else {
        const newList = entityReportList.filter(item => item.id !== data.id);

        if (hasLoadLastPagePagination(newList, pagination)) {
          setQuery(prevState => {
            return { ...prevState, page: pagination?.current - 1 };
          });
        } else if (newList.length == query?.take) {
          await handleRefreshList();
        } else {
          setEntityReportList(newList);
        }
      }
    } catch (error: any) {
      Notification('error', 'Đổi trạng thái thất bại', error);
    } finally {
      setLoadingActionRecordIDs(prevState =>
        prevState.filter(item => item != `${ACTION_TYPE.CHANGE_STATUS}_${id}`)
      );
    }
  };

  const handleOpenDelete = async (id: string) => {
    try {
      setLoadingActionRecordIDs(prevState => [
        ...prevState,
        `${ACTION_TYPE.DELETE}_${id}`,
      ]);
      await dispatch(deleteEntityLarkBase(id)).unwrap();
      const newList = entityReportList.filter(item => item.id !== id);

      if (hasLoadLastPagePagination(newList, pagination)) {
        setQuery(prevState => {
          return { ...prevState, page: pagination?.current - 1 };
        });
      } else if (newList.length == query?.take) {
        await handleRefreshList();
      } else {
        setEntityReportList(newList);
      }
    } catch (error: any) {
      Notification('error', 'Xóa báo cáo thất bại', error);
    } finally {
      setLoadingActionRecordIDs(prevState =>
        prevState.filter(item => item != `${ACTION_TYPE.DELETE}_${id}`)
      );
    }
  };

  const columns: Array<any> = [
    {
      title: 'Tiêu đề',
      width: '40%',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Column>
          <Tooltip title={name}>
            <Text color="primary" lineNumber={1} size="xs" weight="bold">
              {name}
            </Text>
          </Tooltip>
        </Column>
      ),
    },
    {
      width: '20%',
      title: 'Ứng dụng',
      dataIndex: 'entityLarkApp',
      key: 'entityLarkApp',
      render: (entityLarkApp: any) => (
        <Text color="primary" lineNumber={1} size="xs" weight="bold">
          {entityLarkApp.name}
        </Text>
      ),
    },

    {
      width: '20%',
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => (
        <StatusCellTable name="hoạt động" status={status} />
      ),
    },
    {
      width: '20%',
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => (
        <NameText>{formatDate(createdAt, 'DD/MM/YYYY HH:mm')}</NameText>
      ),
    },
    {
      width: 150,
      dataIndex: 'id',
      key: 'id',
      render: (id: string, record: any) => (
        <IconActionTable amountButton={3} data={actionTableData(id, record)} />
      ),
    },
  ];

  const actionTableData = (id: string, record: any) => [
    {
      action: () => {
        dispatch(updateSavedListEntityReport(entityReportList));
        dispatch(updateSavedPaginationEntityReport(pagination));

        history.push(`/settings/stock-report/${id}/update`);
      },
      icon: <EditFilled className="icon" style={{ fontSize: 18 }} />,
      tooltip: 'Chỉnh sửa',
    },
    {
      isLoading: loadingActionRecordIDs.includes(
        `${ACTION_TYPE.CHANGE_STATUS}_${id}`
      ),
      action: () =>
        handleChangeStatus(
          id,
          record.status == ENTITY_REPORT_STATUS.ACTIVATED
            ? ENTITY_REPORT_STATUS.DEACTIVATED
            : ENTITY_REPORT_STATUS.ACTIVATED
        ),
      icon:
        record.status === ENTITY_REPORT_STATUS.ACTIVATED ? (
          <EyeOutlined className="icon" style={{ fontSize: 18 }} />
        ) : (
          <EyeInvisibleOutlined className="icon" style={{ fontSize: 18 }} />
        ),
      tooltip:
        record.status === ENTITY_REPORT_STATUS.DEACTIVATED
          ? 'Bật báo cáo'
          : 'Tắt báo cáo',
    },
    {
      isLoading: loadingActionRecordIDs.includes(`${ACTION_TYPE.DELETE}_${id}`),
      action: () => handleOpenDelete(id),
      icon: <DeleteOutlined className="icon" style={{ fontSize: 18 }} />,
      tooltip: 'Xóa báo cáo',
    },
  ];

  React.useEffect(() => {
    const initData = () => {
      form.setFieldsValue({ status: 'all' });
      form.setFieldsValue({ order: { name: 'createdAt', value: 0 } });
    };
    initData();

    if (checkNotLoadList) {
      setEntityReportList(savedList);
      setPagination(savedPagination);
    }
  }, [form]);

  const locale = {
    emptyText: (
      <Empty
        description={
          <Column alignItems="center" styles={{ gap: 2 }} width="370px">
            <Text size="xs" weight="bold">
              Hiện tại không có báo cáo
            </Text>
            <Text color="secondary" size="xxs" weight="bold">
              Vui lòng nhấp vào nút bên dưới để tạo báo cáo mới
            </Text>
          </Column>
        }
        img={dataEmpty.NOTIFICATION}
        minHeight={320}
      >
        <CButton
          ghost
          type={ButtonType.Primary}
          onClick={() => history.push(`/settings/stock-report/create`)}
        >
          Tạo báo cáo
        </CButton>
      </Empty>
    ),
  };

  return (
    <>
      <Wrapper>
        <FilterTable
          isDisplayFilter
          data={dataFilter}
          form={form}
          handleFilterFormChange={handleFilterFormChange}
          name="Tiêu đề"
          searchName="name"
          searchWidth={280}
        />
        <WrapperTable>
          <Table
            columns={isLoading ? renderTableSkeleton(skeleton) : columns}
            dataSource={
              isLoading ? getDummyArray(10) : [...entityReportList.slice(0, 10)]
            }
            locale={locale}
            pagination={pagination}
            onChange={handlePaginationChange}
          />
        </WrapperTable>
      </Wrapper>
    </>
  );
};

export default EntityStockReportTable;

const Wrapper = styled.div`
  width: 100%;
  height: auto;
  border-radius: 6px;
`;

const WrapperTable = styled.div`
  width: 100%;
`;

const skeleton = [
  {
    title: 'Tiêu đề',
    type: 'input',
    width: 200,
  },
  {
    title: 'Ngày tạo',
    type: 'input',
    width: 100,
  },
  {
    title: 'Trạng thái',
    type: 'input',
    width: 100,
  },
  {
    type: 'input',
    width: 120,
  },
];
